import {
  Component,
  useMemo,
  useState,
  type ErrorInfo,
  type ReactNode,
} from "react"
import { useNavigate } from "react-router"
import { Button } from "@stayframe/ui"
import { cn } from "@/lib/utils"
import { FeedbackTrigger } from "@/components/feedback"
import { usePlanContext } from "@/lib/usePlanContext"
import { usePropertyContext } from "@/lib/usePropertyContext"
import { ContentGeneratorDrawer } from "./ContentGeneratorDrawer"
import { PLACEHOLDER_LISTING_DATA } from "./listing-data"
import "./listings-dashboard.css"
import type {
  AlignmentRow,
  AuditIssue,
  DimensionScore,
  LetterGrade,
  ListingAuditData,
  ListingTab,
} from "./listing-data"
import { useListingIntelligenceData } from "./useListingIntelligenceData"
import {
  AlertTriangle,
  ArrowRight,
  Camera,
  Check,
  Eye,
  Image,
  Loader2,
  RefreshCw,
  ShieldAlert,
  Sparkles,
  Star,
  TrendingDown,
  TrendingUp,
  Wand2,
} from "lucide-react"

type GeneratorType = "title" | "description" | "amenities" | "seasonal"
type ListingsTone = "positive" | "warning" | "critical" | "neutral"
type VerdictTone = "strong" | "steady" | "softening" | "mixed"

type ActionTarget =
  | { type: "generator"; generatorType: GeneratorType }
  | { type: "photo" }
  | { type: "audit" }
  | { type: "route"; href: string }
  | { type: "section"; sectionId: string }

interface ActionItem {
  key: string
  category: string
  title: string
  detail: string
  consequence: string
  tone: ListingsTone
  priority: number
  ctaLabel: string
  target: ActionTarget
}

interface HeroMetric {
  key: string
  label: string
  value: string
  detail: string
  tone?: ListingsTone
  sectionId?: string
}

interface VerdictModel {
  headline: string
  summary: string
  tone: VerdictTone
}

interface ShiftItem {
  key: string
  label: string
  value: string
  detail: string
  tone: ListingsTone
}

interface StudioCard {
  key: GeneratorType
  label: string
  title: string
  body: string
}

const ISSUE_ORDER: Record<AuditIssue["severity"], number> = {
  critical: 0,
  warning: 1,
  suggestion: 2,
}

function safeDate(value: string | null | undefined): Date | null {
  if (!value) return null
  const normalized = value.trim()
  if (!normalized) return null
  const date = new Date(normalized)
  if (Number.isNaN(date.getTime())) return null
  return date
}

function formatRelativeTime(value: string | null | undefined): string {
  const date = safeDate(value)
  if (!date) return "Not audited yet"
  const diffMs = Date.now() - date.getTime()
  const mins = Math.floor(diffMs / 60000)
  if (mins < 1) return "just now"
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 14) return `${days}d ago`
  const weeks = Math.floor(days / 7)
  if (weeks < 8) return `${weeks}w ago`
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" })
}

function pluralize(count: number, singular: string, plural?: string): string {
  const p = plural ?? (/(ch|sh|s|x|z)$/i.test(singular) ? `${singular}es` : `${singular}s`)
  return `${count} ${count === 1 ? singular : p}`
}

function scoreTone(score: number): ListingsTone {
  if (score >= 80) return "positive"
  if (score >= 60) return "warning"
  return "critical"
}

function toneClass(tone: ListingsTone): string {
  if (tone === "positive") return "lst-chip--positive"
  if (tone === "warning") return "lst-chip--warning"
  if (tone === "critical") return "lst-chip--critical"
  return "lst-chip--neutral"
}

function alignmentTone(status: AlignmentRow["status"]): ListingsTone {
  if (status === "confirmed") return "positive"
  if (status === "missed") return "warning"
  return "critical"
}

function alignmentLabel(status: AlignmentRow["status"]): string {
  if (status === "confirmed") return "Guest-backed"
  if (status === "missed") return "Missing from listing"
  return "Not backed"
}

function fixLabel(type: GeneratorType): string {
  if (type === "title") return "Refresh title"
  if (type === "amenities") return "Tighten amenity copy"
  if (type === "seasonal") return "Refresh seasonal copy"
  return "Rewrite copy"
}

function buildVerdict(options: {
  score: number
  weakestDimension: DimensionScore | null
  strongestDimension: DimensionScore | null
  topIssue: AuditIssue | null
  confirmedCount: number
  missedCount: number
  seasonalMismatchCount: number
  hasPhotoAnalysis: boolean
  photoScore: number | null
  auditSummary: string
}): VerdictModel {
  const {
    score,
    weakestDimension,
    strongestDimension,
    topIssue,
    confirmedCount,
    missedCount,
    seasonalMismatchCount,
    hasPhotoAnalysis,
    photoScore,
    auditSummary,
  } = options

  if (score >= 82 && weakestDimension && weakestDimension.score >= 68) {
    return {
      headline: "Strong fundamentals, but the listing is still leaving easy conversion confidence on the table.",
      summary: auditSummary || `The page is broadly strong. ${missedCount > 0 ? `${pluralize(missedCount, "guest-backed strength")} are still missing from the copy.` : "The next gain is sharpening what guests already validate."}`,
      tone: "strong",
    }
  }

  if (!hasPhotoAnalysis && weakestDimension?.key === "photos") {
    return {
      headline: "The listing story is partly blind because photo quality still has not been audited.",
      summary: "Until photos are scored, cover-image strength, room coverage, and visual trust are being guessed rather than managed.",
      tone: "mixed",
    }
  }

  if (seasonalMismatchCount > 0 && weakestDimension?.key === "seasonal") {
    return {
      headline: "Good raw material, but the listing is talking like the wrong season.",
      summary: auditSummary || "Seasonal mismatch is weakening fit with current demand and creating small expectation gaps before guests even click through.",
      tone: "softening",
    }
  }

  if (weakestDimension?.key === "reviews" && missedCount > confirmedCount) {
    return {
      headline: "Guest proof exists, but the listing is not turning it into enough trust.",
      summary: auditSummary || "The copy is missing too much of what guests consistently praise, so conversion confidence stays softer than it should.",
      tone: "mixed",
    }
  }

  if (weakestDimension?.key === "amenities") {
    return {
      headline: "Practical clarity is dragging trust more than the headline copy.",
      summary: auditSummary || "Important operational details are missing or underplayed, which weakens filters, confidence, and price justification.",
      tone: "softening",
    }
  }

  if (weakestDimension?.key === "content") {
    return {
      headline: "The listing has stronger raw proof than the copy is currently expressing.",
      summary: auditSummary || topIssue?.description || "A better title and clearer structure would make the stay feel more bookable before guests ever arrive.",
      tone: "steady",
    }
  }

  if (photoScore != null && photoScore < 60) {
    return {
      headline: "Visual quality is carrying too much risk for the rest of the listing to overcome.",
      summary: auditSummary || "Weak photo confidence undermines the promise the copy is trying to make, especially around trust and first-click conversion.",
      tone: "softening",
    }
  }

  return {
    headline: "This listing is usable, but one weak dimension is holding back conversion quality.",
    summary: auditSummary || `${strongestDimension?.label || "The strongest dimension"} is doing the heavy lifting while ${weakestDimension?.label?.toLowerCase() || "one weak area"} drags the story down.`,
    tone: "steady",
  }
}

function buildActions(options: {
  propertyId: string
  issues: AuditIssue[]
  weakestDimension: DimensionScore | null
  missedClaims: AlignmentRow[]
  unsupportedClaims: AlignmentRow[]
  seasonalMismatchCount: number
  hasPhotoAnalysis: boolean
  hasReviews: boolean
}): ActionItem[] {
  const actions: ActionItem[] = []
  const sortedIssues = [...options.issues].sort((a, b) => ISSUE_ORDER[a.severity] - ISSUE_ORDER[b.severity])
  const topIssue = sortedIssues[0] || null

  const add = (action: ActionItem) => {
    if (actions.some((item) => item.key === action.key)) return
    actions.push(action)
  }

  if (topIssue) {
    const isFixable = Boolean(topIssue.fixable && topIssue.fixType)
    add({
      key: "top_issue",
      category: topIssue.category,
      title: topIssue.title,
      detail: topIssue.description,
      consequence: topIssue.category.toLowerCase() === "seasonal"
        ? "If the listing keeps speaking like the wrong quarter, trust and demand fit soften before guests even read the rest."
        : topIssue.category.toLowerCase() === "reviews"
          ? "When the listing says things guests do not prove, conversion trust erodes faster than scores suggest."
          : "This is the clearest drag on booking confidence right now.",
      tone: topIssue.severity === "critical" ? "critical" : topIssue.severity === "warning" ? "warning" : "neutral",
      priority: topIssue.severity === "critical" ? 100 : topIssue.severity === "warning" ? 92 : 84,
      ctaLabel: isFixable ? fixLabel(topIssue.fixType as GeneratorType) : "Review issue",
      target: isFixable
        ? { type: "generator", generatorType: topIssue.fixType as GeneratorType }
        : { type: "section", sectionId: "lst-issues" },
    })
  }

  if (options.missedClaims.length > 0) {
    add({
      key: "missed_proof",
      category: "Guest proof",
      title: "The listing is not using enough of what guests already validate.",
      detail: `${pluralize(options.missedClaims.length, "missed strength")} are visible in reviews but absent from the listing.`,
      consequence: "This lowers trust and wastes the easiest conversion leverage you already own.",
      tone: "warning",
      priority: 90,
      ctaLabel: "Turn proof into copy",
      target: { type: "generator", generatorType: "description" },
    })
  }

  if (options.seasonalMismatchCount > 0) {
    add({
      key: "seasonal_refresh",
      category: "Seasonal fit",
      title: "Seasonal emphasis needs a refresh before demand context drifts further.",
      detail: `${pluralize(options.seasonalMismatchCount, "seasonal mismatch")} are active in the latest audit.`,
      consequence: "When demand shifts but the listing does not, relevance weakens and the stay promise feels dated.",
      tone: "warning",
      priority: 88,
      ctaLabel: "Refresh seasonal copy",
      target: { type: "generator", generatorType: "seasonal" },
    })
  }

  if (!options.hasPhotoAnalysis) {
    add({
      key: "photo_analysis",
      category: "Visual trust",
      title: "Photo quality is still unscored, so the visual promise is under-read.",
      detail: "Stayframe has not yet checked the cover image, room coverage, or photo balance.",
      consequence: "Without photo analysis, you do not know whether the visual layer is lifting or hurting conversion confidence.",
      tone: "warning",
      priority: 86,
      ctaLabel: "Analyse photos",
      target: { type: "photo" },
    })
  }

  if (!options.hasReviews) {
    add({
      key: "connect_reviews",
      category: "Guest evidence",
      title: "Connect reviews to prove which listing claims are actually landing.",
      detail: "Review alignment is much sharper once the page can compare claims against guest language.",
      consequence: "Without review evidence, the listing can sound polished but still promise the wrong things.",
      tone: "neutral",
      priority: 72,
      ctaLabel: "Open Reviews",
      target: { type: "route", href: `/properties/${options.propertyId}/reviews` },
    })
  }

  if (options.unsupportedClaims.length > 0) {
    add({
      key: "unsupported_claims",
      category: "Expectation setting",
      title: "Some current claims are not backed strongly enough by guest evidence.",
      detail: `${pluralize(options.unsupportedClaims.length, "claim")} look under-supported in the current review record.`,
      consequence: "Claims that guests do not echo weaken trust and can set up softer review outcomes later.",
      tone: "critical",
      priority: 82,
      ctaLabel: "Review alignment",
      target: { type: "section", sectionId: "lst-alignment" },
    })
  }

  if (actions.length === 0) {
    add({
      key: "maintenance",
      category: "Maintenance",
      title: "The listing is in a good place. Re-audit to catch the next drift early.",
      detail: "There is no urgent fix right now, which makes this the ideal moment to protect what is working.",
      consequence: "A calm week is when small promise gaps are easiest to correct before they affect conversion or reviews.",
      tone: "positive",
      priority: 60,
      ctaLabel: "Run fresh audit",
      target: { type: "audit" },
    })
  }

  return actions.sort((a, b) => b.priority - a.priority)
}

function buildShiftItems(options: {
  scoreDelta: number | null
  lastAuditedAt: string | null
  confirmedCount: number
  missedCount: number
  unsupportedCount: number
  seasonalMismatchCount: number
  photoScore: number | null
  weakestDimension: DimensionScore | null
}): ShiftItem[] {
  const items: ShiftItem[] = []

  items.push({
    key: "freshness",
    label: "Audit freshness",
    value: formatRelativeTime(options.lastAuditedAt),
    detail: "Latest listing audit timestamp.",
    tone: options.lastAuditedAt ? "positive" : "warning",
  })

  items.push({
    key: "delta",
    label: "Score movement",
    value: options.scoreDelta == null ? "No baseline" : `${options.scoreDelta > 0 ? "+" : ""}${options.scoreDelta}`,
    detail: options.scoreDelta == null
      ? "This is the first stored listing baseline."
      : options.scoreDelta > 0
        ? "Quality improved since the prior audit."
        : options.scoreDelta < 0
          ? "Quality softened since the prior audit."
          : "No material movement since the prior audit.",
    tone: options.scoreDelta == null ? "neutral" : options.scoreDelta > 0 ? "positive" : options.scoreDelta < 0 ? "warning" : "neutral",
  })

  items.push({
    key: "proof",
    label: "Proof balance",
    value: `${options.confirmedCount}/${options.confirmedCount + options.unsupportedCount + options.missedCount || 0}`,
    detail: options.missedCount > 0
      ? `${pluralize(options.missedCount, "guest-backed strength")} are still missing from the listing.`
      : `${pluralize(options.confirmedCount, "claim")} are clearly backed by guest evidence.`,
    tone: options.missedCount > 0 ? "warning" : options.confirmedCount > 0 ? "positive" : "neutral",
  })

  if (options.seasonalMismatchCount > 0) {
    items.push({
      key: "season",
      label: "Seasonal pressure",
      value: pluralize(options.seasonalMismatchCount, "mismatch"),
      detail: "The current copy is partly out of step with the active demand context.",
      tone: "warning",
    })
  } else if (options.weakestDimension?.key === "seasonal") {
    items.push({
      key: "season",
      label: "Seasonal pressure",
      value: "Watch closely",
      detail: "Seasonal relevance is the weakest dimension even without explicit mismatches.",
      tone: "warning",
    })
  }

  if (options.photoScore != null) {
    items.push({
      key: "photo",
      label: "Photo confidence",
      value: String(options.photoScore),
      detail: "Current photo analysis score.",
      tone: scoreTone(options.photoScore),
    })
  }

  return items.slice(0, 4)
}

function buildStudioCards(options: {
  topIssue: AuditIssue | null
  weakestDimension: DimensionScore | null
  missedClaims: AlignmentRow[]
  seasonalMismatchCount: number
}): StudioCard[] {
  return [
    {
      key: "title",
      label: "Headline",
      title: "Sharpen the first impression",
      body: options.topIssue?.fixType === "title"
        ? options.topIssue.description
        : "Use a stronger title when the listing is underselling location, view, or practical clarity.",
    },
    {
      key: "description",
      label: "Story",
      title: "Turn guest proof into persuasive copy",
      body: options.missedClaims.length > 0
        ? `${pluralize(options.missedClaims.length, "guest-backed strength")} can be promoted more clearly in the description.`
        : "Use the description to turn strengths into trust and booking confidence.",
    },
    {
      key: "amenities",
      label: "Practicals",
      title: "Clarify what guests need to know",
      body: options.weakestDimension?.key === "amenities"
        ? options.weakestDimension.description
        : "Tighten amenity language when practical detail is weakening filters or trust.",
    },
    {
      key: "seasonal",
      label: "Season",
      title: "Reframe the page for current demand",
      body: options.seasonalMismatchCount > 0
        ? `${pluralize(options.seasonalMismatchCount, "seasonal mismatch")} suggest the page is talking like the wrong quarter.`
        : "Seasonal variants keep the listing relevant as guest intent shifts.",
    },
  ]
}

class ListingsErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean; message: string }> {
  state = { hasError: false, message: "" }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, message: error?.message || "Unknown listings render failure" }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("ListingIntelligencePage render failed", error, info)
  }

  render() {
    if (this.state.hasError) {
      return (
        <main className="lst-page">
          <div className="lst-shell">
            <div className="lst-error">
              <p className="lst-error__title">Listings hit an unexpected data issue</p>
              <p className="lst-error__body">
                A live data edge case interrupted the listing dashboard render. Refresh and Stayframe will retry with safer fallbacks.
              </p>
              {this.state.message ? (
                <p className="lst-error__body">
                  <code>{this.state.message}</code>
                </p>
              ) : null}
            </div>
          </div>
        </main>
      )
    }
    return this.props.children
  }
}

function ListingsLoading() {
  return (
    <main className="lst-page">
      <div className="lst-shell">
        <div className="lst-loading" role="status" aria-label="Loading listings dashboard">
          <div className="lst-loading__hero" />
          <div className="lst-loading__grid">
            <div className="lst-loading__panel lst-loading__panel--large" />
            <div className="lst-loading__stack">
              <div className="lst-loading__panel" />
              <div className="lst-loading__panel" />
            </div>
          </div>
          <div className="lst-loading__footer" />
        </div>
      </div>
    </main>
  )
}

function ListingsUpgradeWall({
  propertyName,
  placeholder,
}: {
  propertyName: string
  placeholder: ListingAuditData
}) {
  const navigate = useNavigate()
  const strongest = [...placeholder.dimensions].sort((a, b) => b.score - a.score)[0] || null
  const weakest = [...placeholder.dimensions].sort((a, b) => a.score - b.score)[0] || null

  return (
    <main className="lst-page">
      <div className="lst-shell">
        <section className="lst-upgrade">
          <div className="lst-upgrade__hero">
            <span className="lst-section-kicker">Listings</span>
            <h1 className="lst-empty__title">Unlock the listing quality command center.</h1>
            <p className="lst-empty__body">
              Stayframe can audit the listing against guest proof, seasonal context, amenity clarity, and photo strength so you know exactly what to fix before chasing more demand.
            </p>
            <div className="lst-empty__actions">
              <Button variant="primary" size="lg" onClick={() => navigate("settings?tab=billing")}>
                Upgrade to Insight
              </Button>
            </div>
          </div>

          <div className="lst-upgrade__preview">
            <div className="lst-kpi-grid">
              <div className="lst-kpi">
                <p className="lst-kpi__label">Preview score</p>
                <p className="lst-kpi__value">{placeholder.compositeScore}</p>
                <p className="lst-kpi__detail">Grade {placeholder.grade}</p>
              </div>
              <div className="lst-kpi">
                <p className="lst-kpi__label">Strongest</p>
                <p className="lst-kpi__value">{strongest?.label || "—"}</p>
                <p className="lst-kpi__detail">{strongest ? `${strongest.score}/100` : "Run audit"}</p>
              </div>
              <div className="lst-kpi">
                <p className="lst-kpi__label">Weakest</p>
                <p className="lst-kpi__value">{weakest?.label || "—"}</p>
                <p className="lst-kpi__detail">{weakest ? `${weakest.score}/100` : "Run audit"}</p>
              </div>
            </div>
            <p className="lst-upgrade__note">
              Previewing how {propertyName} would surface listing quality, promise gaps, guest proof, and the first fixes worth making.
            </p>
          </div>
        </section>
      </div>
    </main>
  )
}

function ListingsEmptyState({
  error,
  isAuditing,
  onRunAudit,
}: {
  error: string
  isAuditing: boolean
  onRunAudit: () => void
}) {
  return (
    <section className="lst-empty">
      <div className="lst-empty__content">
        <span className="lst-section-kicker">Listings</span>
        <h1 className="lst-empty__title">Audit the listing before you optimize anything else.</h1>
        <p className="lst-empty__body">
          Stayframe will read the listing like an expert commercial editor: what the page promises, what guests prove, whether the season has shifted, and where trust or conversion confidence is leaking first.
        </p>
        <div className="lst-empty__actions">
          <Button variant="primary" size="lg" onClick={onRunAudit} loading={isAuditing}>
            {isAuditing ? "Auditing…" : "Run first audit"}
          </Button>
        </div>
        {error ? (
          <div className="lst-alert lst-alert--critical" role="alert">
            <AlertTriangle className="h-4 w-4" />
            <p>{error}</p>
          </div>
        ) : null}
      </div>

      <div className="lst-empty__grid">
        <div className="lst-empty-card">
          <p className="lst-empty-card__label">Promise vs proof</p>
          <p className="lst-empty-card__title">See which claims guests actually validate</p>
          <p className="lst-empty-card__body">Review-backed positioning stops the listing from sounding better than the stay feels.</p>
        </div>
        <div className="lst-empty-card">
          <p className="lst-empty-card__label">Seasonal fit</p>
          <p className="lst-empty-card__title">Spot copy that is talking like the wrong quarter</p>
          <p className="lst-empty-card__body">Seasonal drift is one of the easiest ways to lower relevance without noticing.</p>
        </div>
        <div className="lst-empty-card">
          <p className="lst-empty-card__label">Visual quality</p>
          <p className="lst-empty-card__title">Know if the photo set helps or hurts trust</p>
          <p className="lst-empty-card__body">Photo analysis closes the gap between a polished description and the visual proof guests actually see.</p>
        </div>
      </div>
    </section>
  )
}

function ListingsPageFull() {
  const navigate = useNavigate()
  const { propertyId, property } = usePropertyContext()
  const state = useListingIntelligenceData(propertyId)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [drawerContentType, setDrawerContentType] = useState<GeneratorType>("description")
  const [showAllActions, setShowAllActions] = useState(false)

  const sortedDimensions = useMemo(
    () => [...state.dimensions].sort((a, b) => b.score - a.score),
    [state.dimensions],
  )
  const strongestDimension = sortedDimensions[0] || null
  const weakestDimension = sortedDimensions[sortedDimensions.length - 1] || null

  const sortedIssues = useMemo(
    () => [...state.issues].sort((a, b) => ISSUE_ORDER[a.severity] - ISSUE_ORDER[b.severity]),
    [state.issues],
  )
  const topIssue = sortedIssues[0] || null

  const confirmedClaims = useMemo(
    () => state.alignment.filter((row) => row.status === "confirmed"),
    [state.alignment],
  )
  const unsupportedClaims = useMemo(
    () => state.alignment.filter((row) => row.status === "unconfirmed"),
    [state.alignment],
  )
  const missedClaims = useMemo(
    () => state.alignment.filter((row) => row.status === "missed"),
    [state.alignment],
  )

  const verdict = useMemo(
    () =>
      buildVerdict({
        score: state.compositeScore,
        weakestDimension,
        strongestDimension,
        topIssue,
        confirmedCount: confirmedClaims.length,
        missedCount: missedClaims.length,
        seasonalMismatchCount: state.seasonalMismatches.length,
        hasPhotoAnalysis: state.hasPhotoAnalysis,
        photoScore: state.photoAnalysis?.photo_score ?? null,
        auditSummary: state.auditSummary,
      }),
    [
      state.compositeScore,
      weakestDimension,
      strongestDimension,
      topIssue,
      confirmedClaims.length,
      missedClaims.length,
      state.seasonalMismatches.length,
      state.hasPhotoAnalysis,
      state.photoAnalysis?.photo_score,
      state.auditSummary,
    ],
  )

  const actions = useMemo(
    () =>
      propertyId
        ? buildActions({
            propertyId,
            issues: state.issues,
            weakestDimension,
            missedClaims,
            unsupportedClaims,
            seasonalMismatchCount: state.seasonalMismatches.length,
            hasPhotoAnalysis: state.hasPhotoAnalysis,
            hasReviews: state.hasReviews,
          })
        : [],
    [
      propertyId,
      state.issues,
      weakestDimension,
      missedClaims,
      unsupportedClaims,
      state.seasonalMismatches.length,
      state.hasPhotoAnalysis,
      state.hasReviews,
    ],
  )

  const visibleActions = showAllActions ? actions : actions.slice(0, 3)
  const primaryAction = actions[0] || null

  const shiftItems = useMemo(
    () =>
      buildShiftItems({
        scoreDelta: state.scoreDelta,
        lastAuditedAt: state.lastAuditedAt,
        confirmedCount: confirmedClaims.length,
        missedCount: missedClaims.length,
        unsupportedCount: unsupportedClaims.length,
        seasonalMismatchCount: state.seasonalMismatches.length,
        photoScore: state.photoAnalysis?.photo_score ?? null,
        weakestDimension,
      }),
    [
      state.scoreDelta,
      state.lastAuditedAt,
      confirmedClaims.length,
      missedClaims.length,
      unsupportedClaims.length,
      state.seasonalMismatches.length,
      state.photoAnalysis?.photo_score,
      weakestDimension,
    ],
  )

  const studioCards = useMemo(
    () =>
      buildStudioCards({
        topIssue,
        weakestDimension,
        missedClaims,
        seasonalMismatchCount: state.seasonalMismatches.length,
      }),
    [topIssue, weakestDimension, missedClaims, state.seasonalMismatches.length],
  )

  const heroMetrics: HeroMetric[] = [
    {
      key: "score",
      label: "Composite score",
      value: String(state.compositeScore),
      detail: `Grade ${state.grade}`,
      tone: scoreTone(state.compositeScore),
    },
    {
      key: "delta",
      label: "Since last audit",
      value: state.scoreDelta == null ? "No baseline" : `${state.scoreDelta > 0 ? "+" : ""}${state.scoreDelta}`,
      detail: state.lastAuditedAt ? `Audited ${formatRelativeTime(state.lastAuditedAt)}` : "First audit pending",
      tone: state.scoreDelta == null ? "neutral" : state.scoreDelta > 0 ? "positive" : state.scoreDelta < 0 ? "warning" : "neutral",
    },
    {
      key: "strongest",
      label: "Strongest dimension",
      value: strongestDimension?.label || "—",
      detail: strongestDimension ? `${strongestDimension.score}/100` : "No audit yet",
      tone: strongestDimension ? scoreTone(strongestDimension.score) : "neutral",
      sectionId: "lst-dimensions",
    },
    {
      key: "weakest",
      label: "Biggest drag",
      value: weakestDimension?.label || "—",
      detail: weakestDimension ? `${weakestDimension.score}/100` : "No audit yet",
      tone: weakestDimension ? scoreTone(weakestDimension.score) : "neutral",
      sectionId: "lst-issues",
    },
    {
      key: "proof",
      label: "Guest proof",
      value: `${confirmedClaims.length}/${state.alignment.length || 0}`,
      detail: state.hasReviews
        ? `${pluralize(missedClaims.length, "missed strength")} still missing from the listing.`
        : "Connect reviews to sharpen proof.",
      tone: state.hasReviews ? (missedClaims.length > 0 ? "warning" : confirmedClaims.length > 0 ? "positive" : "neutral") : "neutral",
      sectionId: "lst-alignment",
    },
  ]

  function openGenerator(type: GeneratorType) {
    setDrawerContentType(type)
    setDrawerOpen(true)
  }

  async function handleAction(action: ActionItem) {
    if (action.target.type === "generator") {
      openGenerator(action.target.generatorType)
      return
    }
    if (action.target.type === "photo") {
      await state.runPhotoAnalysis()
      return
    }
    if (action.target.type === "audit") {
      await state.runAudit()
      return
    }
    if (action.target.type === "route") {
      navigate(action.target.href)
      return
    }
    document.getElementById(action.target.sectionId)?.scrollIntoView({ behavior: "smooth", block: "start" })
  }

  if (state.loading) {
    return <ListingsLoading />
  }

  if (!propertyId) {
    return (
      <main className="lst-page">
        <div className="lst-shell">
          <div className="lst-error">
            <p className="lst-error__title">Property unavailable</p>
            <p className="lst-error__body">Stayframe could not resolve the property context for this listings workspace.</p>
          </div>
        </div>
      </main>
    )
  }

  return (
    <main className="lst-page">
      <div className="lst-shell">
        {state.error && state.hasAudit ? (
          <div className="lst-alert lst-alert--critical" role="alert">
            <AlertTriangle className="h-4 w-4" />
            <p>{state.error}</p>
          </div>
        ) : null}

        {!state.hasAudit ? (
          <ListingsEmptyState
            error={state.error}
            isAuditing={state.isAuditing}
            onRunAudit={() => void state.runAudit()}
          />
        ) : (
          <>
            <section className="lst-hero">
              <div className="lst-hero__content">
                <div className="lst-hero__eyebrow">
                  <span
                    className={cn(
                      "lst-stage-chip",
                      verdict.tone === "strong" && "lst-stage-chip--positive",
                      verdict.tone === "steady" && "lst-stage-chip--steady",
                      verdict.tone === "softening" && "lst-stage-chip--warning",
                      verdict.tone === "mixed" && "lst-stage-chip--mixed",
                    )}
                  >
                    {verdict.tone === "strong"
                      ? "Strong"
                      : verdict.tone === "softening"
                        ? "Under pressure"
                        : verdict.tone === "mixed"
                          ? "Mixed signal"
                          : "Holding"}
                  </span>
                  <span className="lst-hero__property">{property?.name || "Listing quality"}</span>
                </div>

                {state.listings.length > 1 ? (
                  <div className="lst-tabs">
                    {state.listings.map((listing) => {
                      const active = listing.id === state.activeListingId
                      return (
                        <button
                          key={listing.id}
                          type="button"
                          className={cn("lst-tab", active && "lst-tab--active")}
                          onClick={() => state.setActiveListingId(listing.id)}
                        >
                          <span>{listing.name}</span>
                          <span className="lst-tab__grade">{listing.grade}</span>
                        </button>
                      )
                    })}
                  </div>
                ) : null}

                <div className="lst-hero__copy">
                  <h1 className="lst-hero__headline">{verdict.headline}</h1>
                  <p className="lst-hero__summary">{verdict.summary}</p>
                </div>

                <div className="lst-hero__meta">
                  <span className={cn("lst-chip", toneClass(scoreTone(state.compositeScore)))}>
                    <Star className="h-3.5 w-3.5" />
                    Score {state.compositeScore} / Grade {state.grade}
                  </span>
                  <span className={cn("lst-chip", toneClass(state.scoreDelta != null && state.scoreDelta > 0 ? "positive" : state.scoreDelta != null && state.scoreDelta < 0 ? "warning" : "neutral"))}>
                    {state.scoreDelta != null && state.scoreDelta > 0 ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
                    {state.scoreDelta == null ? "First baseline" : `${state.scoreDelta > 0 ? "+" : ""}${state.scoreDelta} since last audit`}
                  </span>
                  <span className={cn("lst-chip", toneClass(state.seasonalMismatches.length > 0 ? "warning" : "positive"))}>
                    <Sparkles className="h-3.5 w-3.5" />
                    {state.seasonalMismatches.length > 0
                      ? `${pluralize(state.seasonalMismatches.length, "seasonal mismatch")}`
                      : `${state.currentSeason} fit looks clean`}
                  </span>
                  <span className={cn("lst-chip", toneClass(state.hasPhotoAnalysis ? scoreTone(state.photoAnalysis?.photo_score ?? 0) : "warning"))}>
                    <Image className="h-3.5 w-3.5" />
                    {state.hasPhotoAnalysis ? `Photo score ${state.photoAnalysis?.photo_score ?? "—"}` : "Photo blind spot"}
                  </span>
                </div>

                <div className="lst-kpi-grid">
                  {heroMetrics.map((metric) => (
                    <button
                      key={metric.key}
                      type="button"
                      className={cn("lst-kpi", metric.sectionId && "lst-kpi--interactive")}
                      onClick={
                        metric.sectionId
                          ? () => {
                              const sectionId = metric.sectionId
                              if (!sectionId) return
                              document.getElementById(sectionId)?.scrollIntoView({ behavior: "smooth", block: "start" })
                            }
                          : undefined
                      }
                    >
                      <p className="lst-kpi__label">{metric.label}</p>
                      <p className="lst-kpi__value">{metric.value}</p>
                      <p
                        className={cn(
                          "lst-kpi__detail",
                          metric.tone === "positive" && "lst-kpi__detail--positive",
                          metric.tone === "warning" && "lst-kpi__detail--warning",
                          metric.tone === "critical" && "lst-kpi__detail--critical",
                        )}
                      >
                        {metric.detail}
                      </p>
                    </button>
                  ))}
                </div>
              </div>

              <aside className="lst-brief">
                <div className="lst-brief__header">
                  <div>
                    <span className="lst-brief__eyebrow">Listing brief</span>
                    <h2 className="lst-brief__title">What to fix first, and why it matters</h2>
                  </div>
                  <span className="lst-brief__meta">
                    Audited {formatRelativeTime(state.lastAuditedAt)}
                  </span>
                </div>

                <p className="lst-brief__body">
                  {strongestDimension
                    ? `${strongestDimension.label} is carrying the listing, but ${weakestDimension?.label?.toLowerCase() || "one weaker dimension"} is where conversion confidence is leaking first.`
                    : "Run an audit to see which part of the listing is carrying the promise and which part is undermining it."}
                </p>

                <div className="lst-brief__drivers">
                  {strongestDimension ? (
                    <div className="lst-brief__driver">
                      <span className="lst-brief__driver-label">Best-held strength</span>
                      <p>{strongestDimension.description}</p>
                    </div>
                  ) : null}
                  {weakestDimension ? (
                    <div className="lst-brief__driver">
                      <span className="lst-brief__driver-label">Weakest link</span>
                      <p>{weakestDimension.description}</p>
                    </div>
                  ) : null}
                  <div className="lst-brief__driver">
                    <span className="lst-brief__driver-label">Guest evidence</span>
                    <p>
                      {state.hasReviews
                        ? `${pluralize(confirmedClaims.length, "claim")} are supported by guests, while ${pluralize(missedClaims.length, "proof point")} still are not making it into the page.`
                        : "Connect reviews to see whether the listing promise is actually being validated by guests."}
                    </p>
                  </div>
                </div>

                {primaryAction ? (
                  <div className="lst-brief__action">
                    <p className="lst-brief__action-label">Lead move</p>
                    <p className="lst-brief__action-title">{primaryAction.title}</p>
                    <p className="lst-brief__action-detail">{primaryAction.consequence}</p>
                    <Button
                      variant={
                        (primaryAction.target.type === "audit" && state.auditCooldown) ||
                        (primaryAction.target.type === "photo" && state.photoCooldown)
                          ? "ghost" : "primary"
                      }
                      size="lg"
                      onClick={() => void handleAction(primaryAction)}
                      disabled={
                        (primaryAction.target.type === "audit" && state.auditCooldown) ||
                        (primaryAction.target.type === "photo" && state.photoCooldown)
                      }
                      loading={
                        primaryAction.target.type === "audit"
                          ? state.isAuditing
                          : primaryAction.target.type === "photo"
                            ? state.isAnalysingPhotos
                            : false
                      }
                      title={
                        (primaryAction.target.type === "audit" && state.auditCooldown) ||
                        (primaryAction.target.type === "photo" && state.photoCooldown)
                          ? "Limited to once per day" : undefined
                      }
                    >
                      {(primaryAction.target.type === "audit" && state.auditCooldown)
                        ? `Audited ${formatRelativeTime(state.lastAuditedAt)}`
                        : (primaryAction.target.type === "photo" && state.photoCooldown)
                          ? `Analysed ${formatRelativeTime(state.photoAnalysis?.analysed_at)}`
                          : primaryAction.ctaLabel}
                    </Button>
                  </div>
                ) : null}
              </aside>
            </section>

            <section className="lst-main-grid">
              <div className="lst-panel lst-panel--board" id="lst-issues">
                <div className="lst-panel__header">
                  <div>
                    <span className="lst-section-kicker">Issues</span>
                    <h2 className="lst-panel__title">One ranked rail for listing quality decisions</h2>
                  </div>
                  <div className="lst-panel__controls">
                    <Button variant="ghost" size="sm" onClick={() => void state.refresh()}>
                      Refresh audit state
                    </Button>
                    <Button
                      variant={state.auditCooldown ? "ghost" : "secondary"}
                      size="sm"
                      onClick={() => void state.runAudit()}
                      loading={state.isAuditing}
                      disabled={state.auditCooldown || state.isAuditing}
                      title={state.auditCooldown ? "Limited to once per day" : undefined}
                    >
                      {state.auditCooldown ? `Audited ${formatRelativeTime(state.lastAuditedAt)}` : "Re-audit"}
                    </Button>
                  </div>
                </div>

                <div className="lst-action-list">
                  {visibleActions.map((action, index) => (
                    <article
                      key={action.key}
                      className={cn(
                        "lst-action",
                        action.tone === "critical" && "lst-action--critical",
                        action.tone === "warning" && "lst-action--warning",
                        action.tone === "positive" && "lst-action--positive",
                      )}
                    >
                      <div className="lst-action__rank">{String(index + 1).padStart(2, "0")}</div>
                      <div className="lst-action__body">
                        <div className="lst-action__meta">
                          <span>{action.category}</span>
                        </div>
                        <h3 className="lst-action__title">{action.title}</h3>
                        <p className="lst-action__detail">{action.detail}</p>
                        <p className="lst-action__consequence">{action.consequence}</p>
                      </div>
                      <div className="lst-action__cta">
                        <Button
                          variant={index === 0 ? "primary" : "secondary"}
                          onClick={() => void handleAction(action)}
                          loading={
                            action.target.type === "audit"
                              ? state.isAuditing
                              : action.target.type === "photo"
                                ? state.isAnalysingPhotos
                                : false
                          }
                        >
                          {action.ctaLabel}
                        </Button>
                      </div>
                    </article>
                  ))}
                </div>

                {actions.length > 3 ? (
                  <div className="lst-action-list__footer">
                    <Button variant="ghost" size="sm" onClick={() => setShowAllActions((value) => !value)}>
                      {showAllActions ? "Show fewer actions" : `Show all ${actions.length} actions`}
                    </Button>
                  </div>
                ) : null}
              </div>

              <div className="lst-sidebar">
                <div className="lst-panel">
                  <div className="lst-panel__header">
                    <div>
                      <span className="lst-section-kicker">What changed</span>
                      <h2 className="lst-panel__title">Movement that matters since the last audit</h2>
                    </div>
                  </div>

                  <div className="lst-shift-list">
                    {shiftItems.map((item) => (
                      <div key={item.key} className="lst-shift">
                        <div>
                          <p className="lst-shift__label">{item.label}</p>
                          <p className="lst-shift__detail">{item.detail}</p>
                        </div>
                        <span
                          className={cn(
                            "lst-shift__value",
                            item.tone === "positive" && "lst-shift__value--positive",
                            item.tone === "warning" && "lst-shift__value--warning",
                            item.tone === "critical" && "lst-shift__value--critical",
                          )}
                        >
                          {item.value}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="lst-panel" id="lst-dimensions">
                  <div className="lst-panel__header">
                    <div>
                      <span className="lst-section-kicker">Dimension hierarchy</span>
                      <h2 className="lst-panel__title">Not every score matters equally</h2>
                    </div>
                  </div>

                  <div className="lst-dimension-list">
                    {sortedDimensions.map((dimension, index) => (
                      <div
                        key={dimension.key}
                        className={cn(
                          "lst-dimension",
                          index === 0 && "lst-dimension--top",
                          dimension.key === weakestDimension?.key && "lst-dimension--weakest",
                        )}
                      >
                        <div className="lst-dimension__head">
                          <div>
                            <p className="lst-dimension__title">{dimension.label}</p>
                            <p className="lst-dimension__detail">{dimension.description}</p>
                          </div>
                          <div className="lst-dimension__value">
                            <strong>{dimension.score}</strong>
                            <span className={cn("lst-chip", toneClass(scoreTone(dimension.score)))}>{dimension.scoreLabel}</span>
                          </div>
                        </div>
                        <div className="lst-dimension__bar">
                          <span style={{ width: `${Math.max(0, Math.min(100, dimension.score))}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            <section className="lst-lens-grid">
              <div className="lst-panel" id="lst-alignment">
                <div className="lst-panel__header">
                  <div>
                    <span className="lst-section-kicker">Review alignment</span>
                    <h2 className="lst-panel__title">What the listing says vs what guests actually prove</h2>
                  </div>
                  {state.hasReviews ? (
                    <Button variant="secondary" size="sm" onClick={() => openGenerator("description")}>
                      Turn proof into copy
                    </Button>
                  ) : (
                    <Button variant="secondary" size="sm" onClick={() => navigate(`/properties/${propertyId}/reviews`)}>
                      Open Reviews
                    </Button>
                  )}
                </div>

                {state.hasReviews ? (
                  <div className="lst-alignment-grid">
                    <div className="lst-proof-group">
                      <p className="lst-proof-group__title">Backed by guests</p>
                      <div className="lst-proof-list">
                        {confirmedClaims.length > 0 ? confirmedClaims.slice(0, 4).map((row, index) => (
                          <div key={`${row.claim}-${index}`} className="lst-proof lst-proof--positive">
                            <div className="lst-proof__head">
                              <p className="lst-proof__title">{row.claim}</p>
                              <span className={cn("lst-chip", toneClass(alignmentTone(row.status)))}>{alignmentLabel(row.status)}</span>
                            </div>
                            <p className="lst-proof__body">{row.evidence}</p>
                          </div>
                        )) : (
                          <div className="lst-empty-inline">
                            <Check className="h-4 w-4" />
                            <span>No guest-backed claim is clearly standing out yet.</span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="lst-proof-group">
                      <p className="lst-proof-group__title">Not backed strongly enough</p>
                      <div className="lst-proof-list">
                        {unsupportedClaims.length > 0 ? unsupportedClaims.slice(0, 3).map((row, index) => (
                          <div key={`${row.claim}-${index}`} className="lst-proof lst-proof--critical">
                            <div className="lst-proof__head">
                              <p className="lst-proof__title">{row.claim}</p>
                              <span className={cn("lst-chip", toneClass(alignmentTone(row.status)))}>{alignmentLabel(row.status)}</span>
                            </div>
                            <p className="lst-proof__body">{row.evidence}</p>
                          </div>
                        )) : (
                          <div className="lst-empty-inline">
                            <ShieldAlert className="h-4 w-4" />
                            <span>Current claims are broadly supported by the guest record.</span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="lst-proof-group">
                      <p className="lst-proof-group__title">Missing from the listing</p>
                      <div className="lst-proof-list">
                        {missedClaims.length > 0 ? missedClaims.slice(0, 3).map((row, index) => (
                          <div key={`${row.claim}-${index}`} className="lst-proof lst-proof--warning">
                            <div className="lst-proof__head">
                              <p className="lst-proof__title">{row.claim}</p>
                              <span className={cn("lst-chip", toneClass(alignmentTone(row.status)))}>{alignmentLabel(row.status)}</span>
                            </div>
                            <p className="lst-proof__body">{row.evidence}</p>
                          </div>
                        )) : (
                          <div className="lst-empty-inline">
                            <Sparkles className="h-4 w-4" />
                            <span>The listing is using most of the strongest review-backed proof already.</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="lst-empty-inline">
                    <Eye className="h-4 w-4" />
                    <span>Connect reviews to compare listing claims with what guests actually praise or contradict.</span>
                  </div>
                )}
              </div>

              <div className="lst-sidebar">
                <div className="lst-panel" id="lst-season">
                  <div className="lst-panel__header">
                    <div>
                      <span className="lst-section-kicker">Seasonal relevance</span>
                      <h2 className="lst-panel__title">Is the listing speaking to current demand?</h2>
                    </div>
                  </div>
                  <div className="lst-season-callout">
                    <span className={cn("lst-chip", toneClass(state.seasonalMismatches.length > 0 ? "warning" : "positive"))}>
                      {state.currentSeason}
                    </span>
                    <p className="lst-panel__body">
                      {state.hasSeasonProfiles
                        ? state.seasonalEmphasis || "Season profile is connected, but the emphasis note is still building."
                        : "Season profiles are not connected yet, so seasonal copy guidance is still broad."}
                    </p>
                  </div>

                  {state.seasonalMismatches.length > 0 ? (
                    <div className="lst-note-list">
                      {state.seasonalMismatches.slice(0, 3).map((mismatch, index) => (
                        <div key={`${mismatch.text}-${index}`} className="lst-note">
                          <p>{mismatch.text}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="lst-empty-inline">
                      <Check className="h-4 w-4" />
                      <span>No explicit seasonal mismatch is standing out in the current audit.</span>
                    </div>
                  )}

                  {state.upcomingSeason ? (
                    <div className="lst-season-next">
                      <span className="lst-season-next__label">Next shift</span>
                      <p className="lst-season-next__title">
                        {state.upcomingSeason.name} from {state.upcomingSeason.date}
                      </p>
                      <p className="lst-season-next__body">{state.upcomingSeason.emphasis}</p>
                    </div>
                  ) : null}

                  <Button variant="secondary" size="sm" onClick={() => openGenerator("seasonal")}>
                    Refresh seasonal copy
                  </Button>
                </div>

                <div className="lst-panel">
                  <div className="lst-panel__header">
                    <div>
                      <span className="lst-section-kicker">Listing surface</span>
                      <h2 className="lst-panel__title">Where quality is being won or lost</h2>
                    </div>
                  </div>
                  <div className="lst-surface-list">
                    {sortedDimensions.slice(0, 3).map((dimension, index) => (
                      <div key={dimension.key} className="lst-surface">
                        <div>
                          <p className="lst-surface__label">{index === 0 ? "Carrying the page" : index === sortedDimensions.length - 1 ? "Dragging the page" : "Supporting signal"}</p>
                          <h3 className="lst-surface__title">{dimension.label}</h3>
                        </div>
                        <strong>{dimension.score}</strong>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            <section className="lst-lower-grid">
              <div className="lst-panel" id="lst-photo">
                <div className="lst-panel__header">
                  <div>
                    <span className="lst-section-kicker">Photo analysis</span>
                    <h2 className="lst-panel__title">Visual quality should support the promise, not compete with it</h2>
                  </div>
                  {!state.hasPhotoAnalysis ? (
                    <Button variant="secondary" size="sm" onClick={() => void state.runPhotoAnalysis()} loading={state.isAnalysingPhotos}>
                      Analyse photos
                    </Button>
                  ) : state.photoCooldown ? (
                    <span className="text-xs text-muted-foreground" title="Limited to once per day">
                      Analysed {formatRelativeTime(state.photoAnalysis?.analysed_at)}
                    </span>
                  ) : (
                    <Button variant="ghost" size="sm" onClick={() => void state.runPhotoAnalysis()} loading={state.isAnalysingPhotos}>
                      Re-analyse photos
                    </Button>
                  )}
                </div>

                {state.isAnalysingPhotos ? (
                  <div className="lst-empty-inline">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Analysing the photo set now. Stayframe will fold the visual read back into the listing score automatically.</span>
                  </div>
                ) : state.hasPhotoAnalysis && state.photoAnalysis ? (
                  <div className="lst-photo-grid">
                    <div className="lst-photo-callout">
                      <div className="lst-photo-callout__value">
                        <strong>{state.photoAnalysis.photo_score}</strong>
                        <span className={cn("lst-chip", toneClass(scoreTone(state.photoAnalysis.photo_score)))}>{state.photoAnalysis.photo_label}</span>
                      </div>
                      <p className="lst-panel__body">{state.photoAnalysis.photo_summary}</p>
                      <p className="lst-photo-callout__meta">
                        {pluralize(state.photoAnalysis.photo_count, "photo")} analysed • {formatRelativeTime(state.photoAnalysis.analysed_at)}
                      </p>
                    </div>

                    <div className="lst-photo-card">
                      <p className="lst-photo-card__label">Cover photo</p>
                      <h3 className="lst-photo-card__title">{state.photoAnalysis.cover_photo.quality}</h3>
                      <p className="lst-photo-card__body">{state.photoAnalysis.cover_photo.composition}</p>
                      <p className="lst-photo-card__note">{state.photoAnalysis.cover_photo.first_impression}</p>
                    </div>

                    <div className="lst-photo-card">
                      <p className="lst-photo-card__label">Room coverage</p>
                      <h3 className="lst-photo-card__title">{state.photoAnalysis.room_coverage.coverage_pct}% covered</h3>
                      <p className="lst-photo-card__body">
                        Showing: {state.photoAnalysis.room_coverage.rooms_shown.join(", ") || "No room labels"}
                      </p>
                      {state.photoAnalysis.room_coverage.rooms_missing.length > 0 ? (
                        <p className="lst-photo-card__note">
                          Missing: {state.photoAnalysis.room_coverage.rooms_missing.join(", ")}
                        </p>
                      ) : null}
                    </div>

                    <div className="lst-photo-card">
                      <p className="lst-photo-card__label">Photo risks</p>
                      {state.photoAnalysis.photo_issues.length > 0 ? (
                        <div className="lst-note-list">
                          {state.photoAnalysis.photo_issues.slice(0, 3).map((issue, index) => (
                            <div key={`${issue.title}-${index}`} className="lst-note">
                              <p className="lst-note__title">{issue.title}</p>
                              <p>{issue.description}</p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="lst-empty-inline">
                          <Check className="h-4 w-4" />
                          <span>No major photo issue is standing out in the latest run.</span>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="lst-empty-inline">
                    <Camera className="h-4 w-4" />
                    <span>Photo quality is still a blind spot. Analyse the image set to understand cover strength, room coverage, and visual trust.</span>
                  </div>
                )}
              </div>

              <div className="lst-panel" id="lst-studio">
                <div className="lst-panel__header">
                  <div>
                    <span className="lst-section-kicker">Fix studio</span>
                    <h2 className="lst-panel__title">Use AI where the audit says it matters</h2>
                  </div>
                </div>

                <div className="lst-studio-grid">
                  {studioCards.map((card) => (
                    <button key={card.key} type="button" className="lst-studio-card" onClick={() => openGenerator(card.key)}>
                      <p className="lst-studio-card__label">{card.label}</p>
                      <h3 className="lst-studio-card__title">{card.title}</h3>
                      <p className="lst-studio-card__body">{card.body}</p>
                      <span className="lst-studio-card__cta">
                        Open fix
                        <ArrowRight className="h-3.5 w-3.5" />
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </section>
          </>
        )}

        <ContentGeneratorDrawer
          open={drawerOpen}
          onOpenChange={setDrawerOpen}
          contentType={drawerContentType}
          propertyId={propertyId}
          currentSeason={state.currentSeason}
        />

        <FeedbackTrigger pageSlug="listings" />
      </div>
    </main>
  )
}

export function ListingIntelligencePage() {
  const { property } = usePropertyContext()
  const { plan, isLoading } = usePlanContext()

  if (isLoading) return <ListingsLoading />

  if (plan === "host") {
    return (
      <ListingsErrorBoundary>
        <ListingsUpgradeWall
          propertyName={property?.name || "this property"}
          placeholder={PLACEHOLDER_LISTING_DATA}
        />
      </ListingsErrorBoundary>
    )
  }

  return (
    <ListingsErrorBoundary>
      <ListingsPageFull />
    </ListingsErrorBoundary>
  )
}
